import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVideoActivity } from '../providers/VideoActivityProvider';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from './ui';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useConfig } from '../hooks/useConfig';
import { usePlaylistDetail, PlaylistSortOrder, PlaylistDownloadState, PlaylistWatchedState } from '../hooks/usePlaylistDetail';
import { useDownloadListingsRefresh } from '../hooks/useDownloadListingsRefresh';
import { usePlaylistMutations } from '../hooks/usePlaylistMutations';
import { useMediaServerStatus } from '../hooks/useMediaServerStatus';
import { MediaServerType, PlaylistSubscribeSettings, PlaylistVideo } from '../types/playlist';
import PlaylistHeader from './PlaylistPage/components/PlaylistHeader';
import NoMediaServerWarning from './PlaylistPage/components/NoMediaServerWarning';
import PlaylistVideoList from './PlaylistPage/components/PlaylistVideoList';
import PlaylistSortControl from './PlaylistPage/components/PlaylistSortControl';
import PlaylistDownloadFilterControl from './PlaylistPage/components/PlaylistDownloadFilterControl';
import PlaylistWatchedFilterControl from './PlaylistPage/components/PlaylistWatchedFilterControl';
import { useVideoSelection } from './shared/VideoList/hooks/useVideoSelection';
import VideoListSelectionPill from './shared/VideoList/VideoListSelectionPill';
import { SelectionAction } from './shared/VideoList/types';
import { Delete as DeleteIcon, Download as DownloadIcon } from '../lib/icons';
import PlaylistFollowingDialog, { FollowingDialogMode } from './PlaylistPage/components/PlaylistFollowingDialog';
import PlaylistSettingsDialog from './PlaylistPage/components/PlaylistSettingsDialog';
import { isDeletable, isDownloadable, toModalData } from './PlaylistPage/components/playlistVideoHelpers';
import { usePlaylistVideoDeletion } from './PlaylistPage/hooks/usePlaylistVideoDeletion';
import DeleteVideosDialog from './shared/DeleteVideosDialog';
import DownloadSettingsDialog from './DownloadManager/ManualDownload/DownloadSettingsDialog';
import { DownloadSettings } from './DownloadManager/ManualDownload/types';
import SubscriptionsBackButton from './shared/SubscriptionsBackButton';
import VideoModal from './shared/VideoModal';
import { VideoModalData } from './shared/VideoModal/types';

interface PlaylistPageProps {
  token: string | null;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

function PlaylistPage({ token }: PlaylistPageProps) {
  const { id: playlistIdParam } = useParams<{ id: string }>();
  const playlistId = playlistIdParam || null;
  const isMobile = useMediaQuery('(max-width: 767px)');

  const [sortOrder, setSortOrder] = useState<PlaylistSortOrder>('asc');
  const [downloadState, setDownloadState] = useState<PlaylistDownloadState>('all');
  const [watchedState, setWatchedState] = useState<PlaylistWatchedState>('all');

  const {
    playlist,
    videos: playlistVideos,
    downloadedCount,
    notDownloadedCount,
    followingExistingCount,
    followingRequestedCount,
    unsyncableCount,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refetch,
    refetchMeta,
    markVideoIgnored,
    markVideoDeleted,
    refresh,
    sync,
    regenerateM3U,
    triggerDownload,
  } = usePlaylistDetail({ token, playlistId, sortOrder, downloadState, watchedState });

  const { snapshot } = useVideoActivity();
  const videos = useMemo(() => playlistVideos.map(video => ({
    ...video, activity: snapshot.videos[video.youtube_id]?.state,
  })), [playlistVideos, snapshot]);

  useDownloadListingsRefresh(refetch);

  const {
    status: serverStatus,
    anyConfigured,
  } = useMediaServerStatus(token);

  const {
    pending,
    toggleSyncTarget,
    togglePublic,
    toggleAutoDownload,
    ignoreVideo,
    unignoreVideo,
  } = usePlaylistMutations({ token });

  const navigate = useNavigate();
  const { config } = useConfig(token);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<{ mode: 'all' | 'selected'; ids: string[] }>({
    mode: 'all',
    ids: [],
  });

  const [followingMode, setFollowingMode] = useState<FollowingDialogMode | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmPublicOpen, setConfirmPublicOpen] = useState(false);
  const [pendingVideoId, setPendingVideoId] = useState<string | null>(null);
  const [modalVideo, setModalVideo] = useState<VideoModalData | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [runningAction, setRunningAction] = useState<string | null>(null);
  const actionRunning = runningAction !== null;

  const showSnackbar = useCallback((message: string, severity: SnackbarState['severity'] = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // A re-subscribe arrives with router state from AddPlaylistDialog. Show the
  // notice once, then clear the state so refresh or back doesn't repeat it.
  const location = useLocation();
  useEffect(() => {
    const navState = location.state as { restored?: boolean; warning?: string } | null;
    if (navState?.restored || navState?.warning) {
      showSnackbar(navState.warning || 'Playlist restored with its previous settings', navState.warning ? 'warning' : 'success');
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate, showSnackbar]);

  const handleAction = useCallback(
    async (label: string, action: () => Promise<unknown>, successMessage?: string): Promise<boolean> => {
      setRunningAction(label);
      try {
        await action();
        showSnackbar(successMessage ?? `${label} succeeded`);
        return true;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : `${label} failed`;
        showSnackbar(msg, 'error');
        return false;
      } finally {
        setRunningAction(null);
      }
    },
    [showSnackbar]
  );

  const selectionClearRef = useRef<() => void>(() => {});
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // One selection holds downloaded and not-downloaded videos; each action
  // applies to the selected videos it fits.
  const videosById = useMemo(() => new Map(videos.map((v) => [v.youtube_id, v])), [videos]);
  const idsThatFit = useCallback(
    (ids: string[], fits: (video: PlaylistVideo) => boolean) =>
      ids.filter((id) => {
        const video = videosById.get(id);
        return Boolean(video && fits(video));
      }),
    [videosById]
  );

  const deletion = usePlaylistVideoDeletion({ token, markVideoDeleted, refetchMeta });

  const handleDownloadSelected = useCallback((ids: string[]) => {
    const downloadable = idsThatFit(ids, isDownloadable);
    if (!downloadable.length) return;
    setPendingDownload({ mode: 'selected', ids: downloadable });
    setDownloadDialogOpen(true);
  }, [idsThatFit]);

  const handleConfirmDownload = useCallback(
    async (settings: DownloadSettings | null) => {
      setDownloadDialogOpen(false);
      const overrideSettings = settings
        ? {
            resolution: settings.resolution,
            allowRedownload: settings.allowRedownload,
            subfolder: settings.subfolder,
            audioFormat: settings.audioFormat,
            rating: settings.rating,
            skipVideoFolder: settings.skipVideoFolder,
          }
        : undefined;
      const ids = pendingDownload.mode === 'selected' ? pendingDownload.ids.filter(id => !snapshot.videos[id]?.state) : undefined;
      if (ids && !ids.length) return;
      const ok = await handleAction('Download', () => triggerDownload(ids, overrideSettings));
      if (ok) {
        if (pendingDownload.mode === 'selected') selectionClearRef.current();
        navigate('/downloads/activity');
      }
    },
    [pendingDownload, handleAction, triggerDownload, navigate, snapshot]
  );

  const openDownloadAll = useCallback(() => {
    setPendingDownload({ mode: 'all', ids: [] });
    setDownloadDialogOpen(true);
  }, []);

  // Only counted for explicit selections: "download all" targets new videos
  // only, and auto-enabling re-download there would re-fetch the entire
  // playlist (allowRedownload disables the yt-dlp download archive).
  const missingVideoCount = useMemo(() => {
    if (pendingDownload.mode !== 'selected') return 0;
    const ids = new Set(pendingDownload.ids);
    return videos.filter((v) => ids.has(v.youtube_id) && v.previously_downloaded).length;
  }, [pendingDownload, videos]);

  const selectionActions = useMemo<SelectionAction<string>[]>(
    () => [
      {
        id: 'download',
        label: 'Download',
        intent: 'success',
        icon: <DownloadIcon size={14} />,
        disabled: (ids) => idsThatFit(ids, isDownloadable).length === 0,
        onClick: handleDownloadSelected,
      },
      {
        id: 'delete',
        label: 'Delete',
        intent: 'danger',
        icon: <DeleteIcon size={14} />,
        disabled: (ids) => deletion.loading || idsThatFit(ids, isDeletable).length === 0,
        onClick: (ids) => deletion.requestDelete(idsThatFit(ids, isDeletable)),
      },
    ],
    [handleDownloadSelected, idsThatFit, deletion.loading, deletion.requestDelete]
  );

  const selection = useVideoSelection<string>({ actions: selectionActions });
  useEffect(() => {
    const eligible = selection.selectedIds.filter(id => !snapshot.videos[id]?.state);
    if (eligible.length !== selection.selectedIds.length) selection.set(eligible);
  }, [snapshot, selection.selectedIds, selection.set]);

  useEffect(() => {
    selectionClearRef.current = selection.clear;
  }, [selection.clear]);

  // Clear selection when a filter changes so bulk actions can't fire on
  // videos hidden by the new filter. Sort is deliberately excluded: it does
  // not change the selection's eligible set.
  useEffect(() => {
    selection.clear();
  }, [downloadState, watchedState, selection.clear]);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadMore();
        }
      },
      { root: null, rootMargin: '0px 0px 160px 0px', threshold: 0 }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, loadMore]);

  const handleToggleSync = useCallback(
    async (server: MediaServerType, enabled: boolean) => {
      if (!playlist) return;
      const updated = await toggleSyncTarget(playlist.playlist_id, server, enabled);
      if (updated) {
        await refetch();
      }
    },
    [playlist, toggleSyncTarget, refetch]
  );

  const handleToggleAutoDownload = useCallback(
    async (enabled: boolean) => {
      if (!playlist) return;
      if (enabled && !playlist.auto_download_baseline_at) {
        setFollowingMode('setup');
        return;
      }
      const updated = await toggleAutoDownload(playlist.playlist_id, enabled);
      if (updated) {
        await refetch();
        showSnackbar(enabled ? 'Auto-download resumed; new additions will catch up on scheduled runs.' : 'Auto-download paused');
      } else {
        showSnackbar('Could not update auto-download. Please retry.', 'error');
      }
    },
    [playlist, toggleAutoDownload, refetch, showSnackbar]
  );

  const handleConfirmPublic = useCallback(
    async (next: boolean) => {
      if (!playlist) return;
      const updated = await togglePublic(playlist.playlist_id, next);
      if (updated) {
        await refetch();
        showSnackbar(next ? 'Playlist marked public' : 'Playlist marked private');
      }
      setConfirmPublicOpen(false);
    },
    [playlist, togglePublic, refetch, showSnackbar]
  );

  const handleIgnoreVideo = useCallback(
    async (ytId: string) => {
      if (!playlist) return;
      setPendingVideoId(ytId);
      const ok = await ignoreVideo(playlist.playlist_id, ytId);
      setPendingVideoId(null);
      if (ok) {
        // Update the row in place and refresh only the count, so the user's
        // scroll position in a long (paginated) list is preserved.
        markVideoIgnored(ytId, true);
        const isDownloaded = videos.find((v) => v.youtube_id === ytId)?.downloaded;
        showSnackbar(
          isDownloaded
            ? 'Excluded from this playlist. It will be removed from synced server playlists; the file stays on disk.'
            : 'Excluded from this playlist. It won\'t be auto-downloaded.'
        );
        await refetchMeta();
      }
    },
    [playlist, ignoreVideo, markVideoIgnored, refetchMeta, videos, showSnackbar]
  );

  const handleConfirmDelete = async () => {
    const outcome = await deletion.confirmDelete();
    if (!outcome) return;
    showSnackbar(outcome.message, outcome.severity);
    // Nothing deleted: keep the selection so the user can retry.
    if (outcome.deletedIds.length > 0) selection.clear();
  };

  const handleVideoDeleted = useCallback(
    (ytId: string) => {
      markVideoDeleted(ytId);
      void refetchMeta();
    },
    [markVideoDeleted, refetchMeta]
  );

  const handleUnignoreVideo = useCallback(
    async (ytId: string) => {
      if (!playlist) return;
      setPendingVideoId(ytId);
      const ok = await unignoreVideo(playlist.playlist_id, ytId);
      setPendingVideoId(null);
      if (ok) {
        markVideoIgnored(ytId, false);
        showSnackbar('Included in this playlist again.');
        await refetchMeta();
      }
    },
    [playlist, unignoreVideo, markVideoIgnored, refetchMeta, showSnackbar]
  );

  const handleSettingsSaved = useCallback(
    (next: PlaylistSubscribeSettings) => {
      showSnackbar('Playlist settings saved');
      // The playlist meta may not include all settings; refetch to refresh it.
      refetch();
      // Keep next reference to avoid unused-var warning.
      void next;
    },
    [refetch, showSnackbar]
  );

  if (loading && !playlist) {
    return (
      <div>
        <Box className="mb-3">
          <SubscriptionsBackButton tab="playlists" />
        </Box>
        <Box className="py-8 flex justify-center">
          <Typography color="text.secondary">Loading playlist...</Typography>
        </Box>
      </div>
    );
  }

  if (error || !playlist) {
    return (
      <div>
        <Box className="mb-3">
          <SubscriptionsBackButton tab="playlists" />
        </Box>
        <Alert severity="error" className="mt-4">
          {error || 'Playlist not found.'}
        </Alert>
      </div>
    );
  }

  const playlistThumbUrl =
    playlist.thumbnail ||
    (videos[0]?.youtube_id ? `https://i.ytimg.com/vi/${videos[0].youtube_id}/hqdefault.jpg` : '');

  return (
    <div>
      <Box className="mb-3">
        <SubscriptionsBackButton tab="playlists" />
      </Box>

      {!anyConfigured && <NoMediaServerWarning />}

      <PlaylistHeader
        playlist={playlist}
        thumbnailUrl={playlistThumbUrl}
        isMobile={isMobile}
        serverStatus={serverStatus}
        anyConfigured={anyConfigured}
        downloadedCount={downloadedCount}
        newCount={notDownloadedCount}
        followingExistingCount={followingExistingCount}
        followingRequestedCount={followingRequestedCount}
        onChooseExisting={() => setFollowingMode('batch')}
        unsyncableCount={unsyncableCount}
        togglePending={pending}
        actionRunning={actionRunning}
        refreshing={runningAction === 'Refresh'}
        onRefresh={() => handleAction('Refresh', refresh)}
        onDownloadAll={openDownloadAll}
        onOpenSettings={() => setSettingsOpen(true)}
        onToggleAutoDownload={handleToggleAutoDownload}
        onToggleSync={handleToggleSync}
        onChangePublic={() => setConfirmPublicOpen(true)}
        onSyncNow={() => handleAction('Sync', sync, 'Sync started; servers will update shortly')}
        onRegenerateM3U={() => handleAction('M3U regen', regenerateM3U)}
      />

      <Card style={{ borderRadius: 'var(--radius-ui)' }}>
        <CardContent>
          <Box className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Videos
            </Typography>
            <Box className="flex items-center gap-2 flex-wrap">
              <PlaylistDownloadFilterControl
                value={downloadState}
                onChange={setDownloadState}
                disabled={loading && videos.length === 0}
              />
              <PlaylistWatchedFilterControl
                value={watchedState}
                onChange={setWatchedState}
                disabled={loading && videos.length === 0}
              />
              <PlaylistSortControl
                value={sortOrder}
                onChange={setSortOrder}
                disabled={loading && videos.length === 0}
              />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" className="block mb-2">
            Private, members-only, and deleted videos can&apos;t be accessed, so
            they are never loaded or downloaded; you may see fewer videos here
            than YouTube reports for the playlist.
          </Typography>
          <PlaylistVideoList
            videos={videos}
            sortOrder={sortOrder}
            loading={loading}
            onIgnore={handleIgnoreVideo}
            onUnignore={handleUnignoreVideo}
            onVideoClick={(v) => setModalVideo(toModalData(v))}
            pendingId={pendingVideoId}
            isSelected={selection.isSelected}
            onToggle={selection.toggle}
            onSelectAll={(ids) => selection.selectAll(ids)}
            onClearSelection={selection.clear}
          />
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-3">
              {loadingMore && (
                <Typography variant="body2" color="text.secondary">
                  Loading more...
                </Typography>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <PlaylistSettingsDialog
        open={settingsOpen}
        playlist={playlist}
        token={token}
        onClose={() => setSettingsOpen(false)}
        onSaved={handleSettingsSaved}
        onFollowFromNow={() => { setSettingsOpen(false); setFollowingMode('restart'); }}
      />

      {followingMode && (
        <PlaylistFollowingDialog
          key={`${playlist.playlist_id}:${followingMode}`}
          playlist={playlist} token={token} mode={followingMode}
          defaultCount={config.channelFilesToDownload}
          onClose={() => setFollowingMode(null)}
          onSaved={(message) => { setFollowingMode(null); showSnackbar(message, 'info'); void refetch(); }}
        />
      )}

      <DownloadSettingsDialog
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        onConfirm={handleConfirmDownload}
        mode="manual"
        token={token}
        videoCount={
          pendingDownload.mode === 'selected'
            ? pendingDownload.ids.length
            : notDownloadedCount ?? playlist.video_count
        }
        missingVideoCount={missingVideoCount}
        defaultResolution={playlist.video_quality || config.preferredResolution || '1080'}
        defaultAudioFormat={playlist.audio_format}
        defaultAudioFormatSource="playlist"
      />

      <Dialog
        open={confirmPublicOpen}
        onClose={() => setConfirmPublicOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Make playlist {playlist.public_on_servers ? 'private' : 'public'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {playlist.public_on_servers
              ? 'On Jellyfin and Emby, this playlist will no longer be visible to other users. ' +
                'Plex playlists are shared manually, so this setting does not affect them.'
              : 'On Jellyfin and Emby, this playlist will become visible to other users. ' +
                'Plex has no automatic public setting; share Plex playlists with each user inside Plex.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmPublicOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleConfirmPublic(!playlist.public_on_servers)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DeleteVideosDialog
        open={deletion.dialogOpen}
        onClose={deletion.cancelDelete}
        onConfirm={() => { void handleConfirmDelete(); }}
        videoCount={deletion.pendingCount}
        skippedCount={selection.count - deletion.pendingCount}
      />

      {modalVideo && (
        <VideoModal
          open
          onClose={() => setModalVideo(null)}
          video={modalVideo}
          token={token}
          allowIgnore={false}
          onVideoDeleted={handleVideoDeleted}
        />
      )}

      <VideoListSelectionPill selection={selection} isMobile={isMobile} />
    </div>
  );
}

export default PlaylistPage;
